/* What Zana has already finished in this browser: a takeover that reached
   Done, or a question she already answered. The next time the same job or
   the same question comes up she summarises that, instead of starting again.

   Kept per school and seat, for two weeks. A newer result replaces the old
   one for the same job or the same question. */

const FRESH_MS = 14 * 86400000;
const MAX_EACH = 30;

export interface Done {
  id: string;
  ask: string;
  summary: string;
  at: number;
  ok: boolean;
}

interface Store { jobs: Done[]; asks: Done[] }

const keyOf = (org: string, uid: string) => `trasse.zana.done.v1:${org}:${uid}`;

function read(org: string, uid: string): Store {
  if (!org || !uid) return { jobs: [], asks: [] };
  try {
    const raw = localStorage.getItem(keyOf(org, uid));
    if (!raw) return { jobs: [], asks: [] };
    const p = JSON.parse(raw) as Store;
    return { jobs: Array.isArray(p.jobs) ? p.jobs : [], asks: Array.isArray(p.asks) ? p.asks : [] };
  } catch {
    return { jobs: [], asks: [] };
  }
}

function write(org: string, uid: string, store: Store) {
  if (!org || !uid) return;
  try { localStorage.setItem(keyOf(org, uid), JSON.stringify(store)); } catch { /* storage blocked */ }
}

function fresh(row: Done | undefined, now = Date.now()): Done | null {
  if (!row?.ok || !row.summary) return null;
  if (now - row.at > FRESH_MS) return null;
  return row;
}

function put(list: Done[], row: Done): Done[] {
  return [row, ...list.filter((x) => x.id !== row.id)].slice(0, MAX_EACH);
}

/** The words of a question, so "Why is KCE 999Z burning more fuel?" matches the next click of the same button. */
export function askKey(q: string): string {
  return q.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
}

export function rememberJob(org: string, uid: string, row: Done) {
  const store = read(org, uid);
  write(org, uid, { ...store, jobs: put(store.jobs, row) });
}

export function rememberAsk(org: string, uid: string, ask: string, summary: string) {
  const id = askKey(ask);
  if (!id || !summary.trim()) return;
  const store = read(org, uid);
  write(org, uid, { ...store, asks: put(store.asks, { id, ask, summary: summary.trim(), at: Date.now(), ok: true }) });
}

export function recallJob(org: string, uid: string, id: string): Done | null {
  return fresh(read(org, uid).jobs.find((x) => x.id === id));
}

export function recallAsk(org: string, uid: string, ask: string): Done | null {
  const id = askKey(ask);
  if (!id) return null;
  return fresh(read(org, uid).asks.find((x) => x.id === id));
}

export function forgetJob(org: string, uid: string, id: string) {
  const store = read(org, uid);
  write(org, uid, { ...store, jobs: store.jobs.filter((x) => x.id !== id) });
}
